var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React from "react";
import { flexRender } from "@tanstack/react-table";
import styled, { css, useTheme } from "styled-components";
// needed for row & cell level scope DnD setup
import { horizontalListSortingStrategy, SortableContext, useSortable } from "@dnd-kit/sortable";
import { Box } from "../../Box";
import { getCommonDnDStyles, getCommonPinningStyles } from "../util";
const StyledRow = styled(Box).attrs({
    as: "tr",
}) `
  border-bottom: 1px solid;
  border-color: ${({ theme: { colors: { tableColors }, }, }) => tableColors.tableBorder};

  &:hover {
    background-color: ${({ solidColorRowHover, theme: { colors: { tableColors }, }, }) => (solidColorRowHover ? tableColors.tableRowHoverSolid : tableColors.tableRowHover)};
  }
  ${({ isClickable }) => isClickable &&
    css `
      cursor: pointer;
    `}
  ${({ isSelected, solidColorRowHover }) => isSelected &&
    css `
      background-color: ${({ theme: { colors: { tableColors }, }, }) => (solidColorRowHover ? tableColors.tableRowHoverSolid : tableColors.tableRowHover)};
    `}
`;
const getWordBreakSxProps = (column) => column.nowrap ? {} : { wordBreak: "break-word", wordWrap: "break-word" };
const DragAlongCell = ({ cell, props }) => {
    var _a;
    const { isDragging, setNodeRef, transform } = useSortable({
        disabled: !!cell.column.getIsPinned(), // Pinned columns do not get to be dragged and dropped
        id: cell.column.id,
    });
    const { colors: { tableColors }, } = useTheme();
    return (React.createElement(Box, Object.assign({ as: "td", "data-testid": cell.column.columnDef.testId, p: 2, sx: Object.assign(Object.assign(Object.assign(Object.assign({}, getWordBreakSxProps(cell.column.columnDef)), { 
            // support legacy padding
            "&:first-child": {
                p: "8px 8px 8px 18px",
            }, "&:last-child": {
                p: "8px 18px 8px 8px",
            }, backgroundColor: "inherit", textAlign: ((_a = cell.column.columnDef.meta) === null || _a === void 0 ? void 0 : _a.align) || "left" }), getCommonDnDStyles({ isDragging, transform, width: cell.column.getSize() })), getCommonPinningStyles({ boxShadowColor: tableColors.columnPinningBoxShadowColor, column: cell.column })), maxWidth: cell.column.columnDef.maxSize || "initial", minWidth: cell.column.columnDef.minSize || "initial", ref: setNodeRef, role: "cell", width: cell.column.getSize() }, props), flexRender(cell.column.columnDef.cell, cell.getContext())));
};
// TODO: react-table no longer applies "role" styles
export const TableRow = (_a) => {
    var { columnOrder = [], "data-testid": dataTestId = "row", idx, noRowAlternateColors, onClick, row, solidColorRowHover } = _a, props = __rest(_a, ["columnOrder", "data-testid", "idx", "noRowAlternateColors", "onClick", "row", "solidColorRowHover"]);
    const { colors: { tableColors }, } = useTheme();
    const bgColor = idx % 2 ? tableColors.tableRowEven : tableColors.tableRowOdd;
    return (React.createElement(StyledRow, { bg: noRowAlternateColors ? tableColors.tableBackground : bgColor, "data-testid": dataTestId, isClickable: !!onClick, isSelected: row.getIsSelected(), onClick: onClick && ((event) => onClick(row.original, event)), solidColorRowHover: solidColorRowHover, verticalAlign: "middle" }, row.getVisibleCells().map((cell) => (React.createElement(SortableContext, { items: columnOrder, key: cell.id, strategy: horizontalListSortingStrategy },
        React.createElement(DragAlongCell, { cell: cell, props: props }))))));
};
